export const DEFAULT_NEAREST_LIMIT = 20;

export function normalizeCap(value) {
	return (value || '').toString().replace(/\D/g, '').slice(0, 5);
}

export function extractCap(value) {
	const text = (value || '').toString();
	const match = text.match(/(?:^|\D)(\d{5})(?:\D|$)/);
	return match ? match[1] : '';
}

export function getItemAddress(item) {
	return item?.['indirizzo completo'] || item?.indirizzo || '';
}

export function getItemCap(item) {
	const explicitCap = extractCap(item?.cap || item?.['codice postale'] || item?.['cap di interesse']);
	if (explicitCap) return explicitCap;

	const addressCap = extractCap(item?.['indirizzo completo'] || item?.indirizzo || '');
	if (addressCap) return addressCap;

	return normalizeCap(item?.cap || item?.['codice postale'] || item?.['cap di interesse']);
}

export function normalizeCapIndex(rawIndex) {
	if (!rawIndex) return {};

	const source = Array.isArray(rawIndex)
		? rawIndex
		: rawIndex.caps || rawIndex.items || rawIndex.data || rawIndex;

	if (Array.isArray(source)) {
		return source.reduce((acc, entry) => {
			const cap = normalizeCap(entry.cap || entry.postalCode || entry.postal_code || entry.postcode);
			const normalizedEntry = normalizeCapEntry(entry, cap);
			if (cap && normalizedEntry) acc[cap] = normalizedEntry;
			return acc;
		}, {});
	}

	return Object.entries(source).reduce((acc, [key, entry]) => {
		if (key.startsWith('_')) return acc;

		const cap = normalizeCap(entry?.cap || entry?.postalCode || entry?.postal_code || entry?.postcode || key);
		const normalizedEntry = normalizeCapEntry(entry, cap);
		if (cap && normalizedEntry) acc[cap] = normalizedEntry;
		return acc;
	}, {});
}

function normalizeCapEntry(entry, cap) {
	if (!entry || typeof entry !== 'object') return null;

	const lat = parseFloat(entry.lat ?? entry.latitude);
	const lng = parseFloat(entry.lng ?? entry.lon ?? entry.longitude);

	if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

	return {
		...entry,
		cap,
		lat,
		lng,
	};
}

export function getCapLocation(cap, capIndex) {
	const normalized = normalizeCap(cap);
	if (normalized.length !== 5) return null;

	const entry = capIndex?.[normalized];
	if (!entry) return null;

	const lat = parseFloat(entry.lat ?? entry.latitude);
	const lng = parseFloat(entry.lng ?? entry.lon ?? entry.longitude);

	if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

	return {
		lat,
		lng,
		cap: normalized,
		source: entry.source || 'local-cap-index',
		place: entry.place || entry.placeName || '',
		province: entry.province || entry.provinceCode || '',
	};
}

export function getItemCoordinates(item) {
	const lat = parseFloat(item?.latitude);
	const lng = parseFloat(item?.longitude);

	if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

	return { lat, lng };
}

function toRad(value) {
	return value * Math.PI / 180;
}

export function getDistanceKm(origin, destination) {
	if (!origin || !destination) return Number.POSITIVE_INFINITY;

	const earthRadiusKm = 6371;
	const dLat = toRad(destination.lat - origin.lat);
	const dLng = toRad(destination.lng - origin.lng);
	const lat1 = toRad(origin.lat);
	const lat2 = toRad(destination.lat);

	const h =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

	return 2 * earthRadiusKm * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

export function formatDistanceKm(distanceKm) {
	if (typeof distanceKm !== 'number' || Number.isNaN(distanceKm)) return '';
	if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m`;
	return `${distanceKm.toFixed(1).replace('.', ',')} km`;
}

export function buildItemKey(item) {
	return [
		item?.denominazione || '',
		item?.ente || '',
		item?.latitude || '',
		item?.longitude || '',
		getItemAddress(item),
	].join('|');
}

export function getNearestItems(items, origin, options = {}) {
	const limit = options.limit || DEFAULT_NEAREST_LIMIT;
	const maxDistanceKm = options.maxDistanceKm || null;

	if (!origin) return [];

	return items
		.map(item => {
			const coordinates = getItemCoordinates(item);
			if (!coordinates) return null;

			const distanceKm = getDistanceKm(origin, coordinates);
			if (maxDistanceKm && distanceKm > maxDistanceKm) return null;

			return {
				...item,
				_distanceKm: distanceKm,
			};
		})
		.filter(Boolean)
		.sort((a, b) => a._distanceKm - b._distanceKm)
		.slice(0, limit);
}
